'use client';

import { useState, useEffect, useRef } from 'react';

const TIPS = [
  'Tip: Add a year to narrow down results faster',
  'Tip: Use the CNR number for an exact match',
  'Tip: Try the full party name for better results',
  'Search coverage depends on the court details you provide',
  'Tip: Specify the court type — district court or high court',
];

export default function SearchProgress({ notice }: { notice?: string }) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);
  const [showTip, setShowTip] = useState(false);
  const startTimeRef = useRef(0);

  // Elapsed time counter
  useEffect(() => {
    startTimeRef.current = Date.now();
    const timer = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Show tips after 8 seconds
  useEffect(() => {
    const tipTimer = setTimeout(() => setShowTip(true), 8000);
    return () => clearTimeout(tipTimer);
  }, []);

  // Rotate tips
  useEffect(() => {
    if (!showTip) return;
    const interval = setInterval(() => {
      setTipIndex(prev => (prev + 1) % TIPS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [showTip]);

  return (
    <div className="mt-6 w-full max-w-md mx-auto">
      <div className="bg-white border border-primary-100 rounded-2xl px-5 py-4 shadow-sm">
        {/* Current step */}
        <div className="flex items-center gap-3">
          {/* Animated spinner */}
          <div className="relative w-9 h-9 flex-shrink-0">
            <div className="absolute inset-0 rounded-full border-2 border-primary-100" />
            <div className="absolute inset-0 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            <svg className="absolute inset-0 m-auto w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-primary-800">
              Waiting for the court source…
            </p>
            <p className="text-xs text-primary-400 mt-0.5">
              {elapsedSeconds < 5 ? 'Starting search' : `${elapsedSeconds} seconds elapsed`}
            </p>
          </div>
        </div>

        {/* Transport-failure auto-retry notice (set by SmartCaseFinder) */}
        {notice && (
          <div className="mt-3 pt-3 border-t border-amber-100">
            <p className="text-xs font-medium text-amber-700">{notice}</p>
          </div>
        )}

        {/* Tips section - appears after 8s */}
        {showTip && (
          <div className="mt-3 pt-3 border-t border-primary-50">
            <p className="text-xs text-primary-400 transition-opacity duration-500">
              {TIPS[tipIndex]}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
